import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FeatureKey, MeResponse, MembershipSummary } from '@gbv/types';
import { api, setAccessToken, setActiveOrgId } from '../lib/api';

/**
 * Auth and the active organisation. Server data never lives here — that is
 * TanStack Query's job. This store holds identity, the org the user is looking
 * at, and nothing else.
 *
 * Only the chosen org id is persisted. The access token is deliberately not,
 * so a closed tab means a fresh token on return.
 */

interface AuthState {
  user: MeResponse['user'] | null;
  memberships: MembershipSummary[];
  activeOrgId: string | null;
  status: 'unknown' | 'authenticated' | 'anonymous';

  /**
   * `authCode` carries the 6-digit field on the admin sign-in screen. It is
   * sent to the API but not yet enforced — no admin has 2FA enrolled, so the
   * backend accepts a login without it. See the note in LoginPage.tsx.
   */
  login: (email: string, password: string, authCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
  switchOrg: (orgId: string) => void;

  activeMembership: () => MembershipSummary | null;
  can: (feature: FeatureKey) => boolean;
}

interface LoginResponse extends MeResponse {
  accessToken: string;
  expiresIn: number;
}

function applySession(
  set: (partial: Partial<AuthState>) => void,
  get: () => AuthState,
  payload: LoginResponse | MeResponse,
): void {
  if ('accessToken' in payload) setAccessToken(payload.accessToken);

  const memberships = payload.memberships;
  const previous = get().activeOrgId;

  // Keep the chosen org across a refresh when it is still a valid membership.
  const nextOrgId =
    previous && memberships.some((m) => m.orgId === previous)
      ? previous
      : (memberships[0]?.orgId ?? null);

  setActiveOrgId(nextOrgId);

  set({
    user: payload.user,
    memberships,
    activeOrgId: nextOrgId,
    status: 'authenticated',
  });
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      memberships: [],
      activeOrgId: null,
      status: 'unknown',

      login: async (email, password, authCode) => {
        const payload = await api.post<LoginResponse>(
          '/auth/login',
          { email, password, ...(authCode ? { authCode } : {}) },
          { skipOrg: true },
        );
        applySession(set, get, payload);
      },

      logout: async () => {
        await api.post('/auth/logout', undefined, { skipOrg: true }).catch(() => {
          // A failed logout still clears the client; the cookie expires anyway.
        });
        setAccessToken(null);
        setActiveOrgId(null);
        set({ user: null, memberships: [], activeOrgId: null, status: 'anonymous' });
      },

      /** Called once on boot: exchanges the refresh cookie for a live session. */
      restore: async () => {
        try {
          const payload = await api.post<LoginResponse>('/auth/refresh', undefined, {
            skipOrg: true,
          });
          applySession(set, get, payload);
        } catch {
          setAccessToken(null);
          set({ user: null, memberships: [], status: 'anonymous' });
        }
      },

      switchOrg: (orgId) => {
        if (!get().memberships.some((m) => m.orgId === orgId)) return;
        setActiveOrgId(orgId);
        set({ activeOrgId: orgId });
      },

      activeMembership: () => {
        const { memberships, activeOrgId } = get();
        return memberships.find((m) => m.orgId === activeOrgId) ?? null;
      },

      /**
       * Mirrors the backend decision so the UI can hide what the person cannot
       * use. The backend still returns 403 — this is a courtesy, not a control.
       */
      can: (feature) => {
        const membership = get().activeMembership();
        return membership?.featureAccess?.[feature] === true;
      },
    }),
    {
      name: 'gbv-admin-shell',
      partialize: (state) => ({ activeOrgId: state.activeOrgId }),
    },
  ),
);
